import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * useMessageNotifications
 * Polls the messages endpoint at a regular interval and triggers browser + toast + sound
 * notifications whenever a new unread contact message arrives.
 *
 * @param {Object} params
 * @param {Function} params.fetchContacts - async function that returns contacts array
 * @param {boolean} params.enabled - whether polling is active
 * @param {number} params.intervalMs - poll interval (default 30s)
 */
export default function useMessageNotifications({ fetchContacts, enabled = true, intervalMs = 30000 }) {
  // Track the highest known unread count; initialised on first fetch to avoid a burst of notifs on mount.
  const knownIdsRef = useRef(null);
  const initialisedRef = useRef(false);
  const audioCtxRef = useRef(null);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );

  // Lazy audio context creation on first interaction (avoids autoplay blocking)
  const getAudioCtx = () => {
    if (typeof window === "undefined") return null;
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  };

  const requestPermission = async () => {
    if (typeof Notification === "undefined") return "denied";
    const res = await Notification.requestPermission();
    setPermission(res);
    return res;
  };

  const showBrowserNotification = (contact) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    try {
      const n = new Notification("💬 Nouveau message AyaPay", {
        body: `De ${contact.name} · ${contact.email}\n${(contact.message || "").slice(0, 120)}${(contact.message || "").length > 120 ? "…" : ""}`,
        icon: "/apple-touch-icon.png",
        badge: "/favicon-48x48.png",
        tag: `msg-${contact.id}`,
        requireInteraction: false,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch { /* ignored */ }
  };

  const playSound = () => {
    try {
      const ctx = getAudioCtx();
      if (!ctx) return;
      // Two-tone "ding" — two quick sine tones
      const now = ctx.currentTime;
      [880, 1320].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, now + i * 0.12);
        gain.gain.setValueAtTime(0, now + i * 0.12);
        gain.gain.linearRampToValueAtTime(0.15, now + i * 0.12 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + i * 0.12);
        osc.stop(now + i * 0.12 + 0.2);
      });
    } catch { /* ignored */ }
  };

  const notifyNew = (contacts) => {
    contacts.forEach((c) => {
      toast.success(`💬 ${c.name}`, {
        description: (c.message || "").slice(0, 140),
        duration: 8000,
      });
      showBrowserNotification(c);
    });
    if (contacts.length > 0) playSound();
  };

  useEffect(() => {
    if (!enabled || !fetchContacts) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const contacts = await fetchContacts();
        if (cancelled || !Array.isArray(contacts)) return;
        const currentIds = new Set(contacts.map((c) => c.id));
        if (!initialisedRef.current) {
          // First run — just snapshot state, don't fire notifications for existing messages
          knownIdsRef.current = currentIds;
          initialisedRef.current = true;
          return;
        }
        // Find truly new messages (unread and not seen before)
        const newOnes = contacts.filter((c) => !knownIdsRef.current.has(c.id) && !c.read);
        if (newOnes.length > 0) notifyNew(newOnes);
        knownIdsRef.current = currentIds;
      } catch { /* silent */ }
    };

    // Kick off immediately then repeat
    tick();
    const id = setInterval(tick, intervalMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled, fetchContacts, intervalMs]);

  return { permission, requestPermission };
}
